import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AppStateService} from "../../providers/app-state.service";
import {VetineoVisitTypeBundleService} from "../../providers/vetineo-visit-type-bundle.service";
import {Subscription} from "rxjs";
import {VetineoVisitTypeCategory, VetineoVisitTypeSubCategory} from "../../models/vetineo-visit-type-category.model";
import {IonSearchbar, ModalController} from "@ionic/angular";
import {VetClinic} from "../../models/vet-clinic.model";
import {VetClinicService} from "../../providers/vet-clinic.service";
import {CommonUtils} from "../../utils/common";
import {CdkDragDrop} from "@angular/cdk/drag-drop";
import {User} from "../../models/user.model";

@Component({
  selector: 'app-configure-visit-categories-for-vetcode-modal',
  templateUrl: './configure-visit-categories-for-vetcode-modal.component.html',
  styleUrls: ['./configure-visit-categories-for-vetcode-modal.component.scss'],
})
export class ConfigureVisitCategoriesForVetcodeModalComponent implements OnInit, OnDestroy {
  protected mode: 'VETINEO_CATEGORIES' | 'GLOBAL_CATEGORIES' = 'GLOBAL_CATEGORIES';
  private isAdmin: boolean;
  protected isClientAdmin: boolean;
  private isClinicAdmin: boolean;
  hasVetClinicRole: boolean;


  constructor(private appStateService: AppStateService,
              private modalController: ModalController,
              private vetClinicService: VetClinicService,
              private vetineoVisitTypeBundleService: VetineoVisitTypeBundleService,
              private commonUtils: CommonUtils) {
  }

  @Input() vetClinicUuid: string;
  @Input() user: User;
  nameToFind: string;
  openedAccordions: string[] = [];


  vetClinic: VetClinic;
  subscriptions: Subscription = new Subscription();
  globalVetineoVisitTypeCategories: VetineoVisitTypeCategory[];
  vetineoEditedCategories: VetineoVisitTypeCategory[];
  vetineoVisitTypeCategories: VetineoVisitTypeCategory[];
  isLoading: boolean;
  configEditDisabled: boolean;
  @ViewChild('search') search: IonSearchbar;


  async ngOnInit() {
    this.isLoading = true;
    this.isAdmin = this.user.roles.split(';').filter(e => e === 'admin').length === 1;
    this.isClientAdmin = this.isAdmin && this.user.vClientId !== null && this.user.vetcodeId === null;
    this.isClinicAdmin = this.isAdmin && this.user.vClientId !== null && this.user.vetcodeId !== null;
    this.hasVetClinicRole = this.user.roles.split(';').filter(e => e === 'vet-clinics').length === 1 && this.user.vClientId !== null;
    this.configEditDisabled = false;

    try {
      this.vetClinic = await this.vetClinicService.getSpecificVetClinic(this.vetClinicUuid).toPromise();
      this.globalVetineoVisitTypeCategories = await this.vetineoVisitTypeBundleService.getVetineoVisitTypeBundlesCategories().toPromise();
      this.vetineoEditedCategories = [...this.globalVetineoVisitTypeCategories];
      this.vetineoVisitTypeCategories = await this.vetineoVisitTypeBundleService.getVetineoVisitTypeBundlesCategoriesForVetClinic(this.vetClinic.vetcodeId).toPromise();
      this.preselectAlreadyConfiguredCategories();
      this.sortCategories();
      this.isLoading = false;
    } catch (e) {
      this.commonUtils.presentAlertToast("Wystąpił błąd przy ładowaniu danych kategorii");
      this.isLoading = false;
      console.log(e);
    }
  }

  preselectAlreadyConfiguredCategories() {
    this.globalVetineoVisitTypeCategories.forEach((globalCategory) => {
      const matchingCategory = this.vetineoVisitTypeCategories.find(category => category.code === globalCategory.code);
      if (matchingCategory) {
        globalCategory.enabled = true;
        globalCategory.subcategories.forEach((globalSubCategory) => {
          globalSubCategory.enabled = matchingCategory.subcategories.some(category => category.code === globalSubCategory.code);
        })
      }
    });
  }

  filterCategories(categories: VetineoVisitTypeCategory[], search: string): VetineoVisitTypeCategory[] {
    const query = search.toLowerCase();

    return categories
      .map(category => {
        const categoryMatches = category.name.toLowerCase().includes(query);

        const filteredSubcategories = category.subcategories.filter(sub =>
          sub.name.toLowerCase().includes(query)
        );

        if (categoryMatches || filteredSubcategories.length > 0) {
          return {
            ...category,
            subcategories: filteredSubcategories
          };
        }

        return null;
      })
      .filter((category): category is VetineoVisitTypeCategory => category !== null);
  }


  sortCategories() {
    const compareFunc = this.configEditDisabled ? (a: VetineoVisitTypeCategory | VetineoVisitTypeSubCategory, b: VetineoVisitTypeCategory | VetineoVisitTypeSubCategory) => {
      const aEnabled = a.enabled === true ? 1 : 0;
      const bEnabled = b.enabled === true ? 1 : 0;
      return bEnabled - aEnabled;
    } : (a: VetineoVisitTypeCategory | VetineoVisitTypeSubCategory, b: VetineoVisitTypeCategory | VetineoVisitTypeSubCategory) => {
      return a.orderIdx - b.orderIdx;
    }
    this.globalVetineoVisitTypeCategories.sort(compareFunc);
    for (const cat of this.globalVetineoVisitTypeCategories) {
      cat.subcategories.sort(compareFunc)
    }
  }

  private sortVetineoCategories() {
    this.vetineoVisitTypeCategories.forEach(category => {
      category.enabled = true;
      category.subcategories.forEach(subCat => {
        subCat.enabled = true;
      });
    });
    this.vetineoVisitTypeCategories.sort((a, b) => {
      return a.orderIdx - b.orderIdx;
    });

    for (const cat of this.vetineoVisitTypeCategories) {
      cat.subcategories.sort((a, b) => {
        return a.orderIdx - b.orderIdx;
      })
    }
    this.renumberCategories(this.vetineoVisitTypeCategories);
  }

  renumberCategories(categories: VetineoVisitTypeCategory[]) {
    let currentIdx = 1;

    for (let category of categories) {
      category.orderIdx = currentIdx++;

      if (category.subcategories && category.subcategories.length > 0) {
        this.renumberSubCategories(category.subcategories);
      }
    }
  }

  renumberSubCategories(categories: VetineoVisitTypeSubCategory[]) {
    let currentIdx = 1;

    for (let category of categories) {
      category.orderIdx = currentIdx++;
    }
  }


  onClose() {
    this.modalController.dismiss();
  }

  onSelectCategory(category: VetineoVisitTypeCategory) {
    if (this.configEditDisabled) {
      this.commonUtils.presentAlertToast("W celu zmiany konfiguracji skontaktuj się z Biurem Obsługi Klienta", 8000)
      return;
    }

    if (category.enabled) {
      this.appStateService.updateIsBusySubject(true);
      this.registerSubscription(this.vetineoVisitTypeBundleService.checkCategoryBundleExist(this.vetClinic.vetcodeId, category.code).subscribe(canRemove => {
        this.appStateService.updateIsBusySubject(false);
        if (canRemove) {
          this.handleCategorySelected(category);
        } else {
          this.commonUtils.presentAlertToast('Dla danej kategorii zdefiniowany jest typ wizyt i nie można jej usunąć')
        }
      }, error => {
        this.appStateService.updateIsBusySubject(false);
        this.commonUtils.presentAlertToast('Dla danej kategorii zdefiniowany jest typ wizyt i nie można jej usunąć')
      }));

    } else {
      this.handleCategorySelected(category);
    }
  }

  private handleCategorySelected(category: VetineoVisitTypeCategory) {
    category.enabled = !category.enabled;
    if (category.subcategories) {
      for (const inner of category.subcategories) {
        inner.enabled = category.enabled;
      }
    }
  }

  getCategoryStarIconName(category: VetineoVisitTypeCategory | VetineoVisitTypeSubCategory) {

    if (category.enabled) {
      return 'star';
    }
    return 'star-outline';

  }

  onSelectInnerCategory(innerCategory: VetineoVisitTypeSubCategory, category: VetineoVisitTypeCategory) {
    if (this.configEditDisabled) {
      this.commonUtils.presentAlertToast("W celu zmiany konfiguracji skontaktuj się z Biurem Obsługi Klienta", 8000)
      return;
    }

    if (innerCategory.enabled) {
      this.appStateService.updateIsBusySubject(true);
      this.registerSubscription(this.vetineoVisitTypeBundleService.checkSubcategoryBundleExist(this.vetClinic.vetcodeId, category.code, innerCategory.code).subscribe(canRemove => {
        this.appStateService.updateIsBusySubject(false);
        if (canRemove) {
          this.handleSubCategorySelect(innerCategory, category);
        } else {
          this.commonUtils.presentAlertToast('Dla danej kategorii zdefiniowany jest typ wizyt i nie można jej usunąć')
        }
      }, error => {
        this.appStateService.updateIsBusySubject(false);
        this.commonUtils.presentAlertToast('Dla danej kategorii zdefiniowany jest typ wizyt i nie można jej usunąć')
      }));

    } else {
      this.handleSubCategorySelect(innerCategory, category);
    }
  }

  private handleSubCategorySelect(innerCategory: VetineoVisitTypeSubCategory, category: VetineoVisitTypeCategory) {
    innerCategory.enabled = !innerCategory.enabled;
    let anySelected = false;
    const globalCategory = this.globalVetineoVisitTypeCategories.find(cat => cat.code === category.code);
    for (const inner of globalCategory.subcategories) {
      if (inner.enabled) {
        anySelected = true;
      }
    }
    category.enabled = anySelected;

    let categoriesPrepared = this.globalVetineoVisitTypeCategories.map(globalCategory=>{
      let vetineoVisitTypeCategory = this.vetineoEditedCategories.filter(s=>s.code===globalCategory.code)[0];
      if (vetineoVisitTypeCategory) {
        globalCategory.enabled = vetineoVisitTypeCategory.enabled;
        globalCategory.subcategories.forEach(subCategory => {
          let vetineoVisitTypeSubCategory = vetineoVisitTypeCategory.subcategories.filter(a=>a.code===subCategory.code)[0];
          if (vetineoVisitTypeSubCategory) {
            subCategory.enabled = vetineoVisitTypeSubCategory.enabled;
          }
        })
      }
      return globalCategory;
    });

    this.globalVetineoVisitTypeCategories = [...categoriesPrepared];


    this.vetineoEditedCategories = this.vetineoEditedCategories.map(e=> {
      if (e.code===category.code) {
        return category;
      }
      return e;
    });
  }

  existsInAllCategories(code: string, parentCode?: string) {
    const cat = this.globalVetineoVisitTypeCategories.find(c => c.code === (parentCode ?? code));
    if (!cat) return false;
    if (!parentCode) return true;
    return cat.subcategories.some(s => s.code === code);
  }

  openAllAccordions() {
    this.openedAccordions = this.vetineoEditedCategories.map(category => category.name);
  }

  closeAllAccordions() {
    this.openedAccordions = [];
  }


  handleEnabledCategories() {
    this.sortVetineoCategories();

    let enabledCategories = this.globalVetineoVisitTypeCategories.filter(category => category.enabled).map((category: VetineoVisitTypeCategory) => {
      delete category.enabled;
      return category;
    });


    let tmpIdx = 1000;

    let ec = enabledCategories.map(category => {
      const myCategory = this.vetineoVisitTypeCategories.find(c => c.code === category.code);

      const orderIdx = myCategory ? myCategory.orderIdx : ++tmpIdx;

      const subcategories = category.subcategories
        .filter(sub => sub.enabled)
        .map(sub => {
          delete sub.enabled;
          const mySub = myCategory?.subcategories.find(s => s.code === sub.code);
          sub.orderIdx = mySub ? mySub.orderIdx : ++tmpIdx;
          return sub;
        });

      return {
        ...category,
        orderIdx,
        subcategories
      };
    });

    this.vetineoVisitTypeCategories.forEach(category => {
      const existsInMerged = ec.some(c => c.code === category.code);
      const existsInAll = this.globalVetineoVisitTypeCategories.some(c => c.code === category.code);

      if (!existsInMerged && !existsInAll) {
        ec.push({
          ...category,
          orderIdx: category.orderIdx ?? ++tmpIdx,
          subcategories: category.subcategories.map(sub => ({
            ...sub,
            orderIdx: sub.orderIdx ?? ++tmpIdx
          }))
        });
      } else {
        const existing = ec.find(c => c.code === category.code);
        if (existing) {
          category.subcategories.forEach(mySub => {
            const existsSubInMerged = existing.subcategories.some(s => s.code === mySub.code);
            const existsSubInAll = this.globalVetineoVisitTypeCategories
              .find(c => c.code === category.code)
              ?.subcategories?.some(s => s.code === mySub.code);

            if (!existsSubInMerged && !existsSubInAll) {
              existing.subcategories.push({
                ...mySub,
                orderIdx: mySub.orderIdx ?? ++tmpIdx
              });
            }
          });
        }
      }
    });


    this.vetineoVisitTypeCategories = ec;

    this.sortVetineoCategories();
    this.mode = 'VETINEO_CATEGORIES';

  }

  getSubmitTitle() {
    if (this.mode === 'VETINEO_CATEGORIES') {
      return 'zapisz';
    } else {
      return 'ustaw kolejność';
    }
  }

  onSubmit() {
    if (this.mode === 'VETINEO_CATEGORIES') {
      const backup = JSON.stringify(this.globalVetineoVisitTypeCategories);
      const vetClinicUUID = this.vetClinic.uuid;
      const requestObj = {
        vetClinicUUID: vetClinicUUID,
        vetineoVisitTypeCategoriesJson: JSON.stringify(this.vetineoVisitTypeCategories)
      }
      console.log(requestObj);
      this.isLoading = true;
      this.vetClinicService.updateVetineoVisitTypeCategoriesForVetClinic(requestObj).subscribe((response) => {
        if (response) {
          this.commonUtils.presentInfoToast("Konfiguracja kategorii typów wizyt zakończona powodzeniem", "bottom", 5000);
          this.isLoading = false;
          this.modalController.dismiss(null, 'success');
        } else {
          this.commonUtils.presentAlertToast("Wystąpił błąd podczas zapisu konfiguracji", 5000);
          this.globalVetineoVisitTypeCategories = JSON.parse(backup);
          this.isLoading = false;
        }
      }, error => {
        this.commonUtils.presentAlertToast("Wystąpił błąd podczas zapisu konfiguracji", 5000);
        this.globalVetineoVisitTypeCategories = JSON.parse(backup);
        this.isLoading = false;
      })
    } else {
      let categoriesPrepared = this.globalVetineoVisitTypeCategories.map(globalCategory=>{
        let vetineoVisitTypeCategory = this.vetineoEditedCategories.filter(s=>s.code===globalCategory.code)[0];
        if (vetineoVisitTypeCategory) {
          globalCategory.enabled = vetineoVisitTypeCategory.enabled;
          globalCategory.subcategories.forEach(subCategory => {
            let vetineoVisitTypeSubCategory = vetineoVisitTypeCategory.subcategories.filter(a=>a.code===subCategory.code)[0];
            if (vetineoVisitTypeSubCategory) {
              subCategory.enabled = vetineoVisitTypeSubCategory.enabled;
            }
          })
        }
        return globalCategory;
      });
      this.vetineoEditedCategories = [...categoriesPrepared];
      this.handleEnabledCategories();
    }
  }

  drop($event: CdkDragDrop<any, any>, categories: VetineoVisitTypeCategory[] | VetineoVisitTypeSubCategory[]) {
    console.log($event)
    $event.currentIndex++;
    $event.previousIndex++;

    for (let cat of categories) {

      if ($event.previousIndex < $event.currentIndex && cat.orderIdx > $event.previousIndex && cat.orderIdx <= $event.currentIndex) {
        cat.orderIdx -= 1;
      } else if ($event.previousIndex > $event.currentIndex && cat.orderIdx >= $event.currentIndex && cat.orderIdx < $event.previousIndex) {
        cat.orderIdx += 1;
      } else if (cat.orderIdx === $event.previousIndex) {
        cat.orderIdx = $event.currentIndex;
      }
    }

    this.vetineoVisitTypeCategories.sort((a, b) => {
      return a.orderIdx - b.orderIdx;
    });

    for (const cat of this.vetineoVisitTypeCategories) {
      cat.subcategories.sort((a, b) => {
        return a.orderIdx - b.orderIdx;
      })
    }

    this.renumberCategories(this.vetineoVisitTypeCategories);

    categories.sort((a, b) => {
      return a.orderIdx - b.orderIdx;
    });


    console.log(categories)
  }

  registerSubscription(sub: Subscription) {
    if (sub) {
      this.subscriptions.add(sub);
    }
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }

  getSubcategories(subcategories: any) {
    return subcategories.filter(e => e.enabled);
  }

  getCategories() {
    return this.vetineoVisitTypeCategories;
  }

  onFindByEnter() {
    console.log(' ---- onFindByEnter -----')

  }

  prepareFoundItems() {
    if (this.nameToFind.length !== 0 && this.nameToFind.length < 3) {
      return;
    }
    let vvtc = this.filterCategories(this.globalVetineoVisitTypeCategories, this.nameToFind);
    if (this.globalVetineoVisitTypeCategories?.length === vvtc?.length) {
     let categoriesPrepared = this.globalVetineoVisitTypeCategories.map(globalCategory=>{
        let vetineoVisitTypeCategory = this.vetineoEditedCategories.filter(s=>s.code===globalCategory.code)[0];
        if (vetineoVisitTypeCategory) {
          globalCategory.enabled = vetineoVisitTypeCategory.enabled;
          globalCategory.subcategories.forEach(subCategory => {
            let vetineoVisitTypeSubCategory = vetineoVisitTypeCategory.subcategories.filter(a=>a.code===subCategory.code)[0];
            if (vetineoVisitTypeSubCategory) {
              subCategory.enabled = vetineoVisitTypeSubCategory.enabled;
            }
          })
        }
        return globalCategory;
      });

      this.vetineoEditedCategories = [...categoriesPrepared];
      this.closeAllAccordions();
    } else {
      this.vetineoEditedCategories = this.filterCategories(this.globalVetineoVisitTypeCategories, this.nameToFind);
      this.openAllAccordions();
    }

  }

  getGlobalCategories() {
    if (this.nameToFind) {
      return this.filterCategories(this.globalVetineoVisitTypeCategories, this.nameToFind);
    } else {
      return this.globalVetineoVisitTypeCategories;
    }
  }

}
